/**
 * Email Suppressions Tab
 * Admin interface for viewing and removing email addresses on the suppression
 * list (hard bounces + spam complaints recorded by the Resend webhook).
 */

import { LoadingSpinner } from '@components/ui';
import {
  type EmailSuppressionItem,
  type EmailSuppressionReason,
  useAdminEmailSuppressions,
} from '@hooks/useAdminEmailSuppressions';
import { useEmailSuppressionActions } from '@hooks/useEmailSuppressionActions';
import { ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Reason Badge
// =============================================================================

const REASON_LABELS: Record<EmailSuppressionReason, string> = {
  hard_bounce: 'Hard bounce',
  complaint: 'Complaint',
  manual: 'Manual',
};

function ReasonBadge({ reason }: { reason: EmailSuppressionReason }): ReactElement {
  const styles: Record<EmailSuppressionReason, string> = {
    hard_bounce: 'bg-red-500/20 text-red-400 border-red-500/30',
    complaint: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    manual: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };

  return (
    <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${styles[reason]}`}>
      {REASON_LABELS[reason]}
    </span>
  );
}

// =============================================================================
// Component
// =============================================================================

export function EmailSuppressionsTab(): ReactElement {
  const [page, setPage] = useState(1);

  const { suppressions, total, pages, loading, error, refetch } = useAdminEmailSuppressions({
    page,
    limit: 50,
  });

  const { removeSuppression, loading: actionLoading } = useEmailSuppressionActions();

  const handleRemove = async (suppression: EmailSuppressionItem) => {
    const result = await removeSuppression(suppression.email);
    if (result.success) {
      refetch();
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= pages) setPage(newPage);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-theme-text-secondary font-semibold">
          {total} suppressed {total === 1 ? 'address' : 'addresses'}
        </span>
        <p className="text-xs text-theme-text-tertiary">
          Addresses that hard-bounced or marked us as spam. Removing one re-enables email to it.
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-4 bg-theme-danger/10 border border-theme-danger rounded-lg text-theme-danger">
          {error}
        </div>
      )}

      {/* Suppressions Table */}
      {!loading && !error && (
        <>
          <div className="bg-theme-surface rounded-xl border border-theme-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-theme-border">
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Reason
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Detail
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-theme-text-secondary font-medium">
                      Suppressed
                    </th>
                    <th className="px-4 py-3 text-right text-theme-text-secondary font-medium">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-theme-border">
                  {suppressions.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-theme-text-secondary">
                        No suppressed addresses
                      </td>
                    </tr>
                  ) : (
                    suppressions.map((suppression) => (
                      <tr key={suppression.id} className="hover:bg-theme-bg/50 transition-colors">
                        <td className="px-4 py-3 text-white font-mono text-xs">
                          {suppression.email}
                        </td>
                        <td className="px-4 py-3">
                          <ReasonBadge reason={suppression.reason} />
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary text-xs max-w-xs truncate">
                          {suppression.detail || '—'}
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary capitalize">
                          {suppression.source}
                        </td>
                        <td className="px-4 py-3 text-theme-text-secondary text-xs">
                          {formatDate(suppression.createdAt)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              onClick={() => handleRemove(suppression)}
                              disabled={actionLoading}
                              title="Remove from suppression list"
                              className="p-1.5 hover:bg-theme-bg rounded transition-colors text-theme-text-secondary hover:text-theme-danger disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-theme-text-secondary">
                Page {page} of {pages}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  <span>Previous</span>
                </button>
                <button
                  type="button"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pages}
                  className="px-4 py-2 bg-theme-bg border border-theme-border text-theme-text rounded-lg hover:bg-theme-bg-secondary disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <span>Next</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
